import type { Settings } from '@trips/shared/schema';
import { useEffect, useState } from 'react';
import { patch, put } from '../../data/mutate';
import { selectSettings } from '../../data/selectors';
import { getState, useStore } from '../../data/store';
import { api } from '../../lib/api';
import { isNative } from '../../lib/platform';
import { Button } from '../../ui/Button';
import { Toggle } from '../../ui/Field';
import { ListGroup } from '../../ui/List';
import { Page } from '../../ui/Page';
import { toast } from '../../ui/Toast';

type Kind = keyof Settings['notifications'];

/** Which alerts to get. Connections are new, so missing means on. */
const KINDS: { key: Kind; label: string; detail: string }[] = [
  { key: 'delays', label: 'Delays', detail: 'Departure or landing moves 15 minutes or more' },
  { key: 'gates', label: 'Gate and terminal changes', detail: 'Including a gate being assigned' },
  { key: 'cancellations', label: 'Cancellations and diversions', detail: '' },
  {
    key: 'connections',
    label: 'Connections at risk',
    detail: 'Under 45 min (60 abroad) or missed',
  },
  {
    key: 'takeoffLanding',
    label: 'Takeoff and landing',
    detail: 'Landing includes the baggage belt',
  },
  { key: 'checkIn', label: 'Check-in opens', detail: '24 hours before departure' },
];

interface Config {
  vapidPublicKey: string | null;
  telegram: { configured: boolean; linked: boolean };
  webhook: boolean;
}

function setKind(key: Kind, on: boolean) {
  const st = selectSettings(getState());
  const notifications = { ...st.notifications, [key]: on };
  if (getState().tables.settings.get('settings'))
    void patch('settings', 'settings', { notifications });
  else void put('settings', { ...st, notifications });
}

function b64ToBytes(s: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const raw = atob((s + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

async function currentSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

export function NotificationsPage() {
  const st = useStore(selectSettings);
  const [config, setConfig] = useState<Config | null>(null);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const pushSupported = !isNative && 'serviceWorker' in navigator && 'PushManager' in window;

  useEffect(() => {
    void api<Config>('/api/notify/config')
      .then(setConfig)
      .catch(() => {});
    void currentSubscription().then((s) => setSubscribed(!!s));
  }, []);

  const turnOn = async () => {
    if (!config?.vapidPublicKey) return;
    setBusy(true);
    try {
      if ((await Notification.requestPermission()) !== 'granted') {
        toast('Notifications are blocked for this site in the browser settings.', {
          tone: 'error',
        });
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: b64ToBytes(config.vapidPublicKey),
      });
      await api('/api/notify/subscribe', { method: 'POST', body: sub.toJSON() });
      setSubscribed(true);
      toast('Alerts on for this device');
    } catch {
      toast("Couldn't turn on alerts here.", { tone: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    const sub = await currentSubscription();
    if (sub) {
      await api('/api/notify/unsubscribe', { method: 'POST', body: { endpoint: sub.endpoint } });
      await sub.unsubscribe();
    }
    setSubscribed(false);
  };

  const linkTelegram = async () => {
    const r = await api<{ linked: boolean; name?: string }>('/api/notify/telegram/link', {
      method: 'POST',
    });
    if (r.linked) {
      toast(`Telegram linked${r.name ? ` to ${r.name}` : ''}`);
      setConfig((c) => (c ? { ...c, telegram: { ...c.telegram, linked: true } } : c));
    } else toast('Send /start to your bot in Telegram first, then try again.', { tone: 'error' });
  };

  const test = async () => {
    const r = await api<{ push?: number; telegram?: boolean }>('/api/notify/test', {
      method: 'POST',
    });
    const where = [
      r.push ? `${r.push} browser${r.push > 1 ? 's' : ''}` : null,
      r.telegram ? 'Telegram' : null,
    ]
      .filter(Boolean)
      .join(' and ');
    toast(where ? `Sent to ${where}` : 'Nowhere to send it yet', {
      tone: where ? 'neutral' : 'error',
    });
  };

  return (
    <Page title="Flight alerts" backTo="/settings">
      <ListGroup
        title="Where alerts go"
        footer={
          isNative
            ? 'The Android app gets alerts through Telegram. Web alerts work in Chrome and the desktop app.'
            : 'Alerts arrive within seconds of a change being found.'
        }
      >
        {pushSupported && (
          <Toggle
            label="This device"
            detail={
              !config?.vapidPublicKey ? 'Not set up on the server yet' : subscribed ? 'On' : 'Off'
            }
            checked={subscribed}
            onChange={(on) => void (on ? turnOn() : turnOff())}
          />
        )}
        <Toggle
          label="Telegram"
          detail={
            !config?.telegram.configured
              ? 'No bot set up on the server yet'
              : config.telegram.linked
                ? 'Linked'
                : 'Tap to link after sending /start to your bot'
          }
          checked={!!config?.telegram.linked}
          onChange={() => void (config?.telegram.configured ? linkTelegram() : null)}
        />
      </ListGroup>
      <ListGroup title="Tell me about">
        {KINDS.map((k) => (
          <Toggle
            key={k.key}
            label={k.label}
            detail={k.detail || undefined}
            checked={st.notifications[k.key] !== false}
            onChange={(on) => setKind(k.key, on)}
          />
        ))}
      </ListGroup>
      <Button variant="line" icon="bell" disabled={busy} onClick={() => void test()}>
        Send a test alert
      </Button>
    </Page>
  );
}
